import { isEmpty } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { type DropzoneOptions, useDropzone } from "react-dropzone";
import { Trans, useTranslation } from "react-i18next";

import Container from "@vfuk/core-container";
import LinkWithIcon from "@vfuk/core-link-with-icon";

import { Error } from "../Error";
import { Stack } from "../Stack";
import upload from "../assets/svg/upload.svg";
import FileInfo from "./FileInfo";
import { FixedSpan } from "./FixedSpan";
import VerticalDivider from "./VerticalDivider";
import {
  DropZoneStyle,
  FadedImg,
  PreviewLink,
  StyledSection,
  StyledSpan,
} from "./styles";

const iconDimension = "28.5px";

const BYTES_IN_MEGABYTE = 1024 * 1024;

type DropzoneProps = Partial<DropzoneOptions> & {
  uploadDescription?: string;
  uploadButtonLabel?: string;
  files?: File[];
  onChange?: (acceptedFiles: File[]) => void;
  onPreview?: (file: File) => void;
  error?: string;
  allowedFileExtensions?: string[];
  maxFileSize?: number;
};

const Dropzone = ({
  disabled = false,
  multiple,
  onChange,
  onPreview,
  error,
  allowedFileExtensions,
  maxFileSize,
  files: defaultFiles = [],
  ...rest
}: DropzoneProps) => {
  //Remember to import `formsTranslationEN` into your package
  const { t } = useTranslation();

  const [files, setFiles] = useState<File[]>(defaultFiles || []);

  const onDrop: DropzoneOptions["onDrop"] = useCallback(
    (acceptedFiles: File[]) => {
      setFiles((state) => {
        const newState = multiple
          ? [...state, ...acceptedFiles]
          : acceptedFiles;
        onChange?.(newState);
        return newState;
      });
    },
    [onChange]
  );

  useEffect(() => {
    Array.isArray(defaultFiles) && setFiles(defaultFiles);
  }, [defaultFiles]);

  const { getRootProps, getInputProps, open } = useDropzone({
    multiple,
    maxFiles: multiple ? undefined : 1,
    noClick: true,
    noKeyboard: true,
    onDrop,
    ...rest,
  });

  const removeFile = (file: File) => {
    const newFiles = [...files];
    newFiles.splice(newFiles.indexOf(file), 1);
    setFiles(newFiles);
    onChange?.(newFiles);
  };

  return (
    <StyledSection>
      <Stack justifyContent="space-between">
        {t("shared:forms:input:file:dropzone:upload_file")}
      </Stack>
      {!isEmpty(files) && isEmpty(error) && (
        <Container elevation>
          {files.map((file) => (
            <Stack justifyContent="space-between" key={file.name}>
              <Stack>
                <Trans
                  t={t}
                  i18nKey="shared:forms:input:file:dropzone:file_list:item_description"
                  values={{
                    fileName: file.name,
                    fileSize: (file.size / BYTES_IN_MEGABYTE).toFixed(2),
                  }}
                  components={{
                    divider: <VerticalDivider />,
                    bold: <FixedSpan weight={4} />,
                    span: <StyledSpan />,
                  }}
                />
                {onPreview && !error && (
                  <Trans
                    t={t}
                    i18nKey="shared:forms:input:file:dropzone:preview"
                    components={{
                      divider: <VerticalDivider />,
                      link: (
                        <PreviewLink
                          text={t(
                            "shared:forms:input:file:dropzone:preview_button"
                          )}
                          onClick={() => onPreview(file)}
                        />
                      ),
                    }}
                  />
                )}
              </Stack>

              <LinkWithIcon
                appearance="secondary"
                text=""
                icon={{
                  name: "delete",
                  justify: "left",
                }}
                onClick={() => removeFile(file)}
                size={4}
              />
            </Stack>
          ))}
        </Container>
      )}
      {(multiple || isEmpty(files) || !isEmpty(error)) && (
        <div {...getRootProps()}>
          <DropZoneStyle
            isError={!isEmpty(error)}
            {...(disabled ? {} : { onClick: open })}
            isDisabled={disabled}
          >
            <input {...getInputProps()} />
            {disabled ? (
              <FadedImg
                alt=""
                src={upload}
                width={iconDimension}
                height={iconDimension}
              />
            ) : (
              <img
                alt=""
                src={upload}
                width={iconDimension}
                height={iconDimension}
              />
            )}

            {t("shared:forms:input:file:dropzone:upload_button_label")}
          </DropZoneStyle>
        </div>
      )}
      {error ? (
        <Error text={error} />
      ) : (
        <FileInfo
          maxMBFileSize={maxFileSize}
          allowedFileExtensions={allowedFileExtensions}
        />
      )}
    </StyledSection>
  );
};

export default Dropzone;
